/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Page Cashback Container component
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @version 1.13.1
 * @since 1.12.0
 */

import { Fragment, ReactNode, useCallback, useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { LinkBlock, SideMenu, SideMenuItem } from '../../../components/common';
import { ISideMenuItem } from '../../../components/common/SideMenu/SideMenuItem';
import Title from '../../../components/common/Title/Title';
import { pushGTMAnalytic } from '../../../modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../../../modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import PageLocale from '../../../modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../../modules/internationalization/constants';
import {
    makeSelectPageCashbackTranslation,
    makeSelectTranslationCommon,
} from '../../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../../modules/internationalization/types';
import { ECommonTranslationPages } from '../../../modules/internationalization/types/common/pages';
import { IPageCashbackTranslation } from '../../../modules/internationalization/types/pages/cashback';
import { Loader } from '../../../modules/loader/components/Loader';
import { IRootReducer } from '../../../reducers/rootReducer';
import { CLIENT_ID, RoutePath } from '../../../routes/constants/routesConstants';
import { icons as themeIcons } from '../../../theme';
import ErrorPage from '../../ErrorPage/ErrorPage';
import { cashbackGetAccountListStartAction, cashbackGetMetaStartAction } from '../actions/cashbackActions';
import { IPageCashbackReducer } from '../pageCashbackReducer';
import { makeSelectCashbackIsError, makeSelectCashbackIsLoading } from '../selectors/cashbackSelectors';
import { ICashbackGetAccountListStartPayload, ICashbackGetMetaStartPayload } from '../types/cashbackTypes';
import CashbackAccountSelect from './CashbackAccountSelect';
import { PageCashbackStyled } from './styled/CashbackStyled';

interface IPageCashback {
    id: string;
    children: ReactNode;
}

export type TPageCashback = IConnectedProps & IDispatchProps & IPageCashback;

interface IPageCashbackMenuItem extends Pick<ISideMenuItem, 'title'> {
    url: string;
}

const PageCashback = (props: TPageCashback) => {
    const {
        children,
        id,
        isError,
        isLoading,
        translationCashback,
        translationCommon,

        getList,
        getMeta,
        pushGtmAnalytic,
    } = props;

    const location = useLocation();

    const [isAccountSelectVisible, setAccountSelectVisible] = useState(true);
    const [currentTab, setCurrentTab] = useState<IPageCashbackMenuItem>();

    useEffect(() => {
        getMeta({ clientId: id });
    }, [getMeta, id]);

    // load first account list
    useEffect(() => {
        getList({ searchValue: '', start: true });
    }, [getList]);

    // set account select visibility
    useEffect(() => {
        setAccountSelectVisible(true);
    }, []);

    /*
    * -------------------------------------------
    *          Side menu
    * -------------------------------------------
    */

    const getSideMenuItems = useCallback((): IPageCashbackMenuItem[] => [
        {
            url: RoutePath.cashbackControlAccountId.replace(CLIENT_ID, id),
            title: translationCashback.sideMenu.control,
        },
        {
            url: RoutePath.cashbackHistoryAccountId.replace(CLIENT_ID, id),
            title: translationCashback.sideMenu.history,
        },
    ], [translationCashback.sideMenu, id]);

    const clickOnInactiveTab = useCallback((item: IPageCashbackMenuItem) => {
        if (item.url !== currentTab?.url) {
            pushGtmAnalytic({
                operation: GTMOperations.cashback.clickOnInactiveTabCashbackPage(item.title as string),
                withAccountType: true,
                withSusrId: true,
                withUserRole: true,
            });
        }
    }, [currentTab, pushGtmAnalytic]);

    const renderSideMenuItem = useCallback((item: IPageCashbackMenuItem, level = 0) => (
        <LinkBlock key={item.url} url={item.url}>
            <SideMenuItem
                id={item.url}
                isActive={item.url === currentTab?.url}
                level={level}
                title={item.title}
                onClick={() => clickOnInactiveTab(item)}
            />
        </LinkBlock>
    ), [clickOnInactiveTab, currentTab]);

    useEffect(() => {
        setCurrentTab(getSideMenuItems().find(item => item.url === location.pathname));
    }, [getSideMenuItems, location.pathname]);

    /*
    * -------------------------------------------
    *          Render
    * -------------------------------------------
    */

    return (
        <Fragment>
            <PageLocale lazy
                        fileNames={[
                            TranslationFileNamesEnum.common,
                            TranslationFileNamesEnum.corporate,
                            TranslationFileNamesEnum.pageCashback,
                        ]}/>

            <Title pageName={ECommonTranslationPages.cashback}/>

            <PageCashbackStyled>
                <div className="cashback__title">{translationCashback.title}</div>

                <div className="cashback__account-selector">
                    {
                        isAccountSelectVisible && (
                            <CashbackAccountSelect
                                pushGtmAnalytic={props.pushGtmAnalytic}
                                translationCashback={translationCashback}
                                translationCommon={translationCommon}
                            />
                        )
                    }
                </div>

                <div className="cashback__menu">
                    <SideMenu>
                        {getSideMenuItems().map(renderSideMenuItem)}
                    </SideMenu>
                </div>

                <div className="cashback__content">
                    <Loader showLoader={isLoading}>
                        {
                            isError
                                ? <ErrorPage
                                    src={themeIcons.errorDeliveryImage3x}
                                    text={translationCommon.error.ui.text}
                                    title={translationCommon.error.ui.title}
                                />
                                : !isLoading && children
                        }
                    </Loader>
                </div>

            </PageCashbackStyled>
        </Fragment>
    );
};

export interface IConnectedProps extends Pick<IPageCashbackReducer, 'isError' | 'isLoading'> {
    translationCashback: IPageCashbackTranslation;
    translationCommon: ICommonTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isError: makeSelectCashbackIsError(),
    isLoading: makeSelectCashbackIsLoading(),
    translationCashback: makeSelectPageCashbackTranslation(),
    translationCommon: makeSelectTranslationCommon(),
});

export interface IDispatchProps {
    getList: (payload: ICashbackGetAccountListStartPayload) => void;
    getMeta: (payload: ICashbackGetMetaStartPayload) => void;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    getList: payload => dispatch(cashbackGetAccountListStartAction(payload)),
    getMeta: payload => dispatch(cashbackGetMetaStartAction(payload)),
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PageCashback);
